// UnlockButton.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import { TranslationNamespace } from '../resource/translationNamespace.js';

import Spinning from '../style/Spinning.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import ButtonShimmerStyle from '../style/ButtonShimmerStyle.js';
import { color } from '../style/variables.js';

import DiamondIcon from '../../img/ic-diamond-md.svg';
import LoadingImage from '../../img/loading.png';

/**
 * @typedef {'authorized' | 'unauthorized' | 'loading'} Status
 *
 * @param {Object} props
 * @param {Status} props.status
 */
export const UnlockButton = ({
  status,
  price = null,
  t,
  i18nKey = null,
  shouldShowPrice = true,
  shouldShowShimmer = false,
  ...props
}) => {
  switch (status) {
    case 'authorized':
      return (
        <StyledUnlockButton
          role="button"
          shouldShowShimmer={shouldShowShimmer}
          {...props}
        >
          {i18nKey
            ? t(i18nKey, {
                ns: TranslationNamespace.POST,
              })
            : t('message_detail_unlock', {
                ns: TranslationNamespace.POST,
              })}

          {shouldShowPrice && (
            <>
              <Divider />
              <DiamondImg src={DiamondIcon} alt="diamond" />
              {price}
            </>
          )}
        </StyledUnlockButton>
      );
    case 'unauthorized':
      return price > 0 ? (
        <StyledUnlockButton
          role="button"
          shouldShowShimmer={shouldShowShimmer}
          {...props}
        >
          {i18nKey
            ? t(i18nKey, {
                ns: TranslationNamespace.POST,
              })
            : t('guest_message_detail_unlock', {
                ns: TranslationNamespace.POST,
              })}
        </StyledUnlockButton>
      ) : null;
    case 'loading':
    default: {
      const LoadingImageSrc = LoadingImage.src || LoadingImage;
      return (
        <StyledUnlockButton
          isFilled
          role="button"
          shouldShowShimmer={shouldShowShimmer}
          {...props}
        >
          <LoadingImg src={LoadingImageSrc} alt="loading" />
        </StyledUnlockButton>
      );
    }
  }
};

UnlockButton.propTypes = {
  status: PropTypes.oneOf(['authorized', 'unauthorized', 'loading']),
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  i18nKey: PropTypes.string,
  shouldShowPrice: PropTypes.bool,
  shouldShowShimmer: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

const StyledUnlockButton = styled(
  ({ isFilled, shouldShowShimmer, ...restProps }) => <button {...restProps} />
).attrs(({ isFilled }) => ({
  style: {
    backgroundColor: isFilled ? color.tealBlue : color.neutral['30_alpha_30'],
    color: isFilled ? color.neutral[0] : color.tealBlue,
  },
}))`
  ${ResetButtonStyle};
  ${({ shouldShowShimmer }) => shouldShowShimmer && ButtonShimmerStyle};
  --button-shine-color: ${color.tealBlue_alpha_10};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid ${color.tealBlue};
  padding: 0px 8px;
  width: 100%;
  height: 36px;
  font-size: 18px;
  font-weight: 600;
  & > img {
    width: 18px;
    height: 18px;
  }
`;

const DiamondImg = styled.img`
  margin-right: 3px;
`;

const LoadingImg = styled.img`
  animation: 1s infinite linear ${Spinning};
`;

const Divider = styled.div`
  background-color: ${color.tealBlue};
  width: 1px;
  height: 18px;
  margin: 0 4px;
`;

export default withTranslation()(UnlockButton);
